/**
 * docxXmlParser.ts
 * XML-surgery DOCX parser — extracts text while preserving XML positions.
 * On export, translated text is injected back into the original XML.
 */
import JSZip from 'jszip'

export interface XmlSegment {
  source: string           // Plain text for display/translation
  paragraphIndex: number   // Index of <w:p> in document.xml (for export)
  target?: string
}

/**
 * Extract all non-empty paragraphs from a DOCX file.
 * Returns plain text + paragraph index for each segment.
 */
export async function parseDocxXml(file: File): Promise<XmlSegment[]> {
  const arrayBuffer = await file.arrayBuffer()
  const zip = await JSZip.loadAsync(arrayBuffer)

  const xmlStr = await zip.file('word/document.xml')?.async('string')
  if (!xmlStr) throw new Error('Cannot read document.xml from DOCX')

  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlStr, 'application/xml')
  const paragraphs = Array.from(doc.getElementsByTagNameNS(
    'http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'p'
  ))

  const segments: XmlSegment[] = []

  paragraphs.forEach((p, index) => {
    // Extract all <w:t> text content from this paragraph
    const textNodes = Array.from(p.getElementsByTagNameNS(
      'http://schemas.openxmlformats.org/wordprocessingml/2006/main', 't'
    ))
    const text = textNodes.map(t => t.textContent || '').join('').trim()

    if (text.length > 0) {
      segments.push({
        source: text,
        paragraphIndex: index,
        target: ''
      })
    }
  })

  return segments
}

/**
 * Inject translated text back into the original DOCX XML.
 * Only <w:t> nodes are modified — all formatting, images, tables preserved.
 */
export async function exportDocxXml(
  originalFileUrl: string,
  translations: Record<number, string>  // paragraphIndex → translated text
): Promise<Blob> {
  // Fetch original DOCX — try direct fetch first, then with no-cors
  let arrayBuffer: ArrayBuffer
  try {
    const response = await fetch(originalFileUrl, {
      method: 'GET',
      headers: { 'Cache-Control': 'no-cache' }
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    arrayBuffer = await response.arrayBuffer()
  } catch (fetchErr) {
    throw new Error(`Failed to fetch original DOCX file: ${fetchErr}. Check Supabase Storage bucket is public.`)
  }

  const zip = await JSZip.loadAsync(arrayBuffer)
  const xmlStr = await zip.file('word/document.xml')?.async('string')
  if (!xmlStr) throw new Error('Cannot read document.xml')

  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlStr, 'application/xml')
  const NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main'
  const paragraphs = Array.from(doc.getElementsByTagNameNS(NS, 'p'))

  paragraphs.forEach((p, index) => {
    const translation = translations[index]
    if (!translation) return  // No translation for this paragraph — leave untouched

    const textNodes = Array.from(p.getElementsByTagNameNS(NS, 't'))
    if (textNodes.length === 0) return

    // Put full translated text into first <w:t>, clear the rest
    // This preserves the run's formatting (bold, italic, etc.) from the first run
    textNodes[0].textContent = translation
    for (let i = 1; i < textNodes.length; i++) {
      textNodes[i].textContent = ''
    }
  })

  // Serialize back to XML string
  const serializer = new XMLSerializer()
  const newXml = serializer.serializeToString(doc)

  // Replace document.xml in the ZIP
  zip.file('word/document.xml', newXml)

  // Generate and return the new DOCX blob
  return zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
}
